import { useEffect, useRef, useCallback } from "react";
import * as THREE from "three";

function AnimatedBackground({ intensity = 0.3 }) {
  const mountRef = useRef(null);
  const frameRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);

  // Debounced resize handler for performance
  const resizeTimeoutRef = useRef(null);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    }, 100);
  }, []);

  useEffect(() => {
    const mountNode = mountRef.current;
    if (!mountNode) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;
    cameraRef.current = camera;

    // Renderer setup with performance optimizations
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false, // Disable for better performance
      powerPreference: "high-performance",
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Lower pixel ratio for performance
    renderer.setClearColor(0x000000, 0);
    mountNode.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create particle system - adaptive count based on device
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile
      ? 60
      : Math.min(120, Math.floor(window.innerWidth / 12));
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    // Cyan and purple color palette matching theme
    const cyan = new THREE.Color(0x00f2ff);
    const purple = new THREE.Color(0xbc13fe);

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;

      // Random positions
      positions[i3] = (Math.random() - 0.5) * 20;
      positions[i3 + 1] = (Math.random() - 0.5) * 20;
      positions[i3 + 2] = (Math.random() - 0.5) * 10;

      // Random colors between cyan and purple
      const colorMix = Math.random();
      const color = new THREE.Color().lerpColors(cyan, purple, colorMix);
      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;

      // Random sizes
      sizes[i] = Math.random() * 0.5 + 0.2;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));

    // Shader material for smooth, glowing particles
    // Note: Do NOT declare 'attribute vec3 color;' - Three.js auto-includes it when vertexColors: true
    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        intensity: { value: intensity },
      },
      vertexShader: `
        attribute float size;
        varying vec3 vColor;
        varying float vSize;
        uniform float time;
        
        void main() {
          vColor = color;
          vSize = size;
          
          vec3 pos = position;
          // Gentle floating animation
          pos.y += sin(time * 0.5 + position.x * 0.1) * 0.3;
          pos.x += cos(time * 0.3 + position.y * 0.1) * 0.2;
          
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        uniform float intensity;
        
        void main() {
          float distanceToCenter = distance(gl_PointCoord, vec2(0.5));
          float alpha = 1.0 - smoothstep(0.0, 0.5, distanceToCenter);
          alpha *= intensity;
          
          // Glow effect
          float glow = exp(-distanceToCenter * 3.0) * intensity;
          
          gl_FragColor = vec4(vColor + glow * 0.5, alpha * 0.6);
        }
      `,
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // Add geometric shapes for depth (only on desktop for performance)
    const shapes = [];
    if (!isMobile) {
      const addGeometricShape = (shapeGeom, shapeColor, position, scale) => {
        const edges = new THREE.EdgesGeometry(shapeGeom);
        const line = new THREE.LineSegments(
          edges,
          new THREE.LineBasicMaterial({
            color: shapeColor,
            transparent: true,
            opacity: 0.1 * intensity,
          })
        );
        line.position.copy(position);
        line.scale.setScalar(scale);
        scene.add(line);
        return line;
      };

      const box = new THREE.BoxGeometry(2, 2, 2);
      const octa = new THREE.OctahedronGeometry(1.5);

      shapes.push(
        addGeometricShape(box, 0x00f2ff, new THREE.Vector3(-3, 2, -2), 1)
      );
      shapes.push(
        addGeometricShape(octa, 0xbc13fe, new THREE.Vector3(3, -2, -3), 1.2)
      );
    }

    // Animation loop with frame timing
    let time = 0;
    let lastTime = performance.now();

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);

      // Delta time for consistent animation speed
      const currentTime = performance.now();
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      time += deltaTime;

      // Update particle material time
      material.uniforms.time.value = time;
      particles.rotation.y += 0.0005 * deltaTime * 60;

      // Rotate geometric shapes slowly
      shapes.forEach((shape, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        shape.rotation.x += 0.002 * direction * deltaTime * 60;
        shape.rotation.y += 0.003 * direction * deltaTime * 60;
      });

      renderer.render(scene, camera);
    };

    animate();

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      if (mountNode && rendererRef.current?.domElement) {
        mountNode.removeChild(rendererRef.current.domElement);
      }

      // Dispose of Three.js resources
      geometry.dispose();
      material.dispose();

      shapes.forEach((shape) => {
        shape.geometry.dispose();
        shape.material.dispose();
      });

      renderer.dispose();
    };
  }, [intensity, handleResize]);

  return (
    <div
      ref={mountRef}
      className="fixed inset-0 pointer-events-none z-0"
      aria-hidden="true"
    />
  );
}

export default AnimatedBackground;
